import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import type { IGetAiAgentModelListParams } from '../../../../../../../shared/apis/ai-agent/model/types';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';

import type { IAiAgentModelFilterForm, TAiAgentModelSearchType } from '../types';

const initialFilterForm: IAiAgentModelFilterForm = {
  searchType: 'assignedName',
  useYn: 'all',
};

const isAiAgentModelSearchType = (value: string | null): value is TAiAgentModelSearchType =>
  value === 'assignedName' || value === 'productGroup' || value === 'modifier';

const parseUseYnFilter = (value: string | null): IAiAgentModelFilterForm['useYn'] => {
  if (value === 'Y' || value === 'N') {
    return value;
  }

  return 'all';
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentModelFilters() {
  const inputRef = useRef<HTMLInputElement>(null);
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams('');
  const [filterForm, setFilterForm] = useState<IAiAgentModelFilterForm>(initialFilterForm);
  const searchTypeValue = searchParams.get('searchType');
  const searchTypeParam = isAiAgentModelSearchType(searchTypeValue) ? searchTypeValue : initialFilterForm.searchType;
  const keywordParam = searchParams.get('keyword') || '';
  const useYnParam = parseUseYnFilter(searchParams.get('useYn'));

  const queryParams: IGetAiAgentModelListParams = {
    companyId: parsePositiveInteger(companyId ?? null, 0),
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
    searchType: searchTypeParam,
    keyword: keywordParam || undefined,
    useYn: useYnParam === 'all' ? undefined : useYnParam,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentModelFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const handleSearchType = (value: string | null) => {
    updateFilterForm({
      searchType: isAiAgentModelSearchType(value) ? value : initialFilterForm.searchType,
    });
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFilters = () => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        searchType: filterForm.searchType,
        keyword: inputRef.current?.value || '',
        useYn: filterForm.useYn === 'all' ? '' : filterForm.useYn,
      }),
    );
  };

  const handleSearch = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.type === 'keyup' && (e as React.KeyboardEvent<HTMLInputElement>).key !== 'Enter') {
      return;
    }

    applyFilters();
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  const resetFilters = () => {
    resetSearchInput();
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        searchType: initialFilterForm.searchType,
        keyword: '',
        useYn: '',
      }),
    );
  };

  useEffect(() => {
    setFilterForm({
      searchType: searchTypeParam,
      useYn: useYnParam,
    });

    if (inputRef.current) {
      inputRef.current.value = keywordParam;
    }
  }, [keywordParam, useYnParam, searchTypeParam]);

  return {
    filterForm,
    queryParams,
    inputRef,
    updateFilterForm,
    handleSearchType,
    handleSearch,
    handlePageChange,
    resetSearchInput,
    resetFilters,
  };
}
